import { i18n } from '@lingui/core'
import { msg, Trans } from '@lingui/macro'
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  RadioGroup
} from '@mui/material'
import Checkbox from 'app/views/common-components/CustomCheckbox'
import Radio from 'app/views/common-components/CustomRadio'
import { ErrorLabel } from 'app/views/common-components/forms/ErrorLabel'
import { TooltipLabelIcon } from 'app/views/common-components/TooltipLabelIcon'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import RequiredLabelPrefix from 'app/views/common/labels/RequiredLabelPrefix'
import { FastField, useFormikContext } from 'formik'
import _, { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { languages, myI18n } from 'translation/I18nConnectedProvider'
import { hasFieldValue } from 'utilsTS'
import {
  checkShouldUpdateForMultiuserField,
  handleSelectActions,
  parseFormLabelText
} from '../../common/Common'
import { commitChangeToMultipleFields } from '../../multiuser/grpcMultiuserEdit'
import { parseLabel } from '../../parseLabel'
import { FormPicklistReadOnly } from './FormPicklistReadOnly'

/**
 * Form element which renders a list of selecable options based on Material UI. Depending on variant it can render either a single-select Radio Group, single-select Dropdown or multi-select Checkbox Group
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {boolean}  [typeProps.required=false] If providing input to this field should be required in the form.
 * @param  {boolean}  [typeProps.readOnly=false] If field should be render in read only view.
 * @param  {string} [typeProps.picklistType='singleselect'] Type of picklist that should be rendered. Avaliable options are: 'singleselect', 'expandable' and 'multiselect'.
 * @param  {boolean}  [typeProps.dynamic] If true, instead of using options defined in editor, the options definitions will be fetched from the Salesforce from connected field.
 * @param  {Object[]} [typeProps.options] Options defined in editor, that will be rendered as avaliable to pick in the element.
 * @param  {number}  [typeProps.maxOptions] For 'multiselect' variant - how many options at max can be selected.
 * @param  {number}  [typeProps.componentWidth] For 'expandable' variant - width in pixels that the field shoud have.
 */
export const FormPicklist = ({
  id,
  tooltip,
  useMultiuser,
  muBag,
  typeProps,
  value,
  disabled,
  langVersion,
  connectedFieldDetails,
  i18n,
  ...props
}) => {
  const {
    options = [],
    picklistType,
    required,
    requiredAll,
    readOnly,
    isConnected,
    componentWidth,
    dynamic,
    maxOptions,
    isRow,
    numberOfColumns
  } = typeProps
  const otherId = 'other.' + id
  const hasTooltip = Boolean(tooltip)
  const { values, setValues } = useFormikContext()

  const selectableOptions =
    dynamic && connectedFieldDetails && connectedFieldDetails.picklistValues
      ? connectedFieldDetails.picklistValues
        .filter(item => item.active)
        .map((item, index) => {
          return {
            title: item.label,
            apiValue: item.value
          }
        })
      : options.map(item => {
        return {
          ...item,
          tooltip: parseFormLabelText({
            text: item.tooltip,
            i18n,
            langVersion
          }),
          title: parseFormLabelText({
            text: item.title,
            i18n,
            langVersion
          })
          // String(item.tooltip || '').replace(/\u00a0/g, ' ')
        }
      })

  const optionsMap = {}
  selectableOptions.forEach((option, index) => {
    const defaultOpt = 'option' + index
    const optionValue = isConnected ? option.apiValue || defaultOpt : defaultOpt
    selectableOptions[index].orgIndex = index
    optionsMap[optionValue] = option
  })
  const isLongOptionTitle = selectableOptions.some(
    option => option.title && option.title.length > 100
  )
  const areMoreColumnsAvailable = picklistType !== 'expandable' && !isRow
  let baseNumberOfColumns

  if (numberOfColumns && numberOfColumns !== 'default') {
    baseNumberOfColumns = Number(numberOfColumns)
  } else if (areMoreColumnsAvailable) {
    if (isLongOptionTitle) {
      baseNumberOfColumns = 1
    } else {
      baseNumberOfColumns = selectableOptions.length > 10 ? 2 : 1
    }
  } else {
    baseNumberOfColumns = 1
  }

  const [numberOfColumnsToUse, setNumberOfColumnsToUse] =
    useState(baseNumberOfColumns)
  const formGroupRef = useRef(null)
  useEffect(() => {
    if (
      numberOfColumns ||
      !areMoreColumnsAvailable ||
      !formGroupRef.current ||
      isLongOptionTitle
    ) { return }

    const handleResize = debounce(() => {
      if (!formGroupRef.current) return

      const width = formGroupRef.current.clientWidth
      const newColumnCount = width < 800 ? 1 : (selectableOptions?.length > 10 ? 2 : 1)

      if (numberOfColumnsToUse !== newColumnCount) {
        setNumberOfColumnsToUse(newColumnCount)
      }
    }, 300)

    const resizeObserver = new ResizeObserver(handleResize)

    resizeObserver.observe(formGroupRef.current)

    return () => resizeObserver.disconnect()
  }, [areMoreColumnsAvailable, numberOfColumnsToUse, selectableOptions?.length])

  if (readOnly) {
    return (
      <FormPicklistReadOnly
        {...props}
        title={null}
        id={id}
        typeProps={typeProps}
        value={value}
        disabled={disabled}
        langVersion={langVersion}
        connectedFieldDetails={connectedFieldDetails}
      />
    )
  }

  if (picklistType === 'multiselect') {
    const listValue = value || []
    const clearOptionSelected =
      listValue.length === 1 &&
      selectableOptions.some(item => {
        const index = item.orgIndex
        const defaultOpt = 'option' + index
        const optionValue = isConnected
          ? item.apiValue || defaultOpt
          : defaultOpt
        if (optionValue === listValue[0] && item.clearOnSelect) {
          return true
        } else {
          return false
        }
      })

    return (
      <PicklistMultiselectMemoized
        id={id}
        options={selectableOptions}
        langVersion={langVersion}
        required={required}
        requiredAll={requiredAll}
        disabled={disabled}
        maxOptions={maxOptions}
        isConnected={isConnected}
        clearOptionSelected={clearOptionSelected}
        muBag={muBag}
        useMultiuser={useMultiuser}
        numberOfColumns={numberOfColumnsToUse}
        isRow={isRow}
        formGroupRef={formGroupRef}
        areMoreColumnsAvailable={areMoreColumnsAvailable}
      />
    )
  } else if (picklistType === 'expandable') {
    const requireDetails = optionsMap[value]?.requireDetails
    return (
      <div>
        <TextFieldWithFormik
          // useMultiuser={useMultiuser}
          select
          // muBag={muBag}
          name={id}
          style={{
            marginTop: !hasTooltip && 5,
            width: componentWidth
          }}
          fullWidth={!componentWidth}
          variant='outlined'
          disabled={selectableOptions.length === 0 || disabled}
          required={required}
          // multiuserEventOnChange
          onChange={e => {
            const toSet = { ...values }
            const oldValue = toSet[id]
            let lastOptionActions = []
            if (oldValue) {
              selectableOptions.some(option => {
                const index = option.orgIndex
                const defaultOpt = 'option' + index
                const optionValue = isConnected
                  ? option.apiValue || defaultOpt
                  : defaultOpt
                if (oldValue === optionValue) {
                  lastOptionActions = option.selectActions
                }
                return oldValue === optionValue
              })
            }
            toSet[id] = e.target.value
            toSet[otherId] = ''
            // const modified = handleSelectActions({
            //   toSet,
            //   checked: true,
            //   lastOptionActions,
            //   selectActions: optionsMap[toSet[id]]?.selectActions
            // })
            setValues(toSet)
            // if (useMultiuser) {
            //   commitChangeToMultipleFields({
            //     array: [...modified, id, otherId].map(id => [id, toSet[id]]),
            //     ...muBag
            //   })
            // }
          }}
        >
          {selectableOptions.map(item => {
            const index = item.orgIndex
            const defaultOpt = 'option' + index
            const optionValue = isConnected
              ? item.apiValue || defaultOpt
              : defaultOpt
            return (
              <MenuItem key={index} value={optionValue}>
                {item.title}
              </MenuItem>
            )
          })}
        </TextFieldWithFormik>
        {requireDetails && (
          <div style={{ marginTop: 5 }}>
            <RequireDetailsField
              item={optionsMap[value]}
              useMultiuser={useMultiuser}
              muBag={muBag}
              id={otherId}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    )
  }
  return (
    <PicklistRadiogroupMemoized
      id={id}
      options={selectableOptions}
      langVersion={langVersion}
      required={required}
      disabled={disabled}
      isConnected={isConnected}
      muBag={muBag}
      useMultiuser={useMultiuser}
      otherId={otherId}
      numberOfColumns={numberOfColumnsToUse}
      isRow={isRow}
      formGroupRef={formGroupRef}
      areMoreColumnsAvailable={areMoreColumnsAvailable}
    />
  )
}

export const getPicklistRenderProps = ({
  connectedFieldDetails,
  langVersion,
  typeProps
}) => {
  const { options = [] } = typeProps
  const valueToTrans = {}
  const valueToIndex = {}
  const valueToSFDetails = {}
  options.forEach((opt, index) => {
    const value = opt.apiValue || `option${index}`
    valueToTrans[value] = parseFormLabelText({
      text: opt.title,
      langVersion
    })
    valueToIndex[value] = index
  })
  if (connectedFieldDetails && connectedFieldDetails.picklistValues) {
    connectedFieldDetails.picklistValues.forEach((option, index) => {
      valueToSFDetails[String(option.value).replace(/\s/g, ' ')] = {
        label: option.label,
        connectedField: option.connectedFieldDetails,
        index
      }
    })
  }
  options.forEach((opt, index) => {
    if (valueToSFDetails[opt.apiValue]) {
      valueToSFDetails[opt.apiValue].connectedField = opt.connectedFieldDetails
    }
  })
  return { valueToTrans, valueToIndex, valueToSFDetails }
}

export const formPicklistValueToText = (value = [], question, object = {}) => {
  const { connectedFieldDetails } = object
  const { dynamic } = question.typeProps
  const options =
    dynamic && connectedFieldDetails
      ? connectedFieldDetails.picklistValues
        .filter(item => item.active)
        .map((item, index) => {
          const title = languages.reduce((acc, lang) => {
            acc[lang] = myI18n.t(item.label)
            return acc
          }, {})
          return {
            title,
            apiValue: item.value
          }
        })
      : question.typeProps.options

  const selected = languages.reduce((acc, lang) => {
    acc[lang] = []
    return acc
  }, {})
  if (Array.isArray(value)) {
    value.forEach(v => {
      options.some((option, index) => {
        const found = v === 'option' + index || v === option.apiValue
        if (found) {
          languages.forEach(lang => {
            selected[lang].push(option.title[lang])
          })
        }
        return found
      })
    })
    const _selected = languages.reduce((acc, lang) => {
      acc[lang] = (
        <div>
          {selected[lang].map(v => (
            <div>{v}</div>
          ))}
        </div>
      )
      return acc
    }, {})
    return _selected
  } else {
    const singleOption = languages.reduce((acc, lang) => {
      acc[lang] = ''
      return acc
    }, {})
    options.some((option, index) => {
      let found
      if (option.apiValue) {
        found = value === option.apiValue
      } else {
        found = value === 'option' + index
      }
      if (found) {
        languages.forEach(lang => {
          singleOption[lang] = option.title[lang]
        })
      }
      return found
    })
    const _singleOption = languages.reduce((acc, lang) => {
      acc[lang] = <div>{singleOption[lang]}</div>
      return acc
    }, {})
    return _singleOption
  }
}

const PicklistRadiogroupMemoized = ({
  id,
  options,
  langVersion,
  required,
  disabled,
  isConnected,
  otherId,
  muBag,
  useMultiuser,
  numberOfColumns,
  isRow,
  formGroupRef,
  areMoreColumnsAvailable
}) => {
  const manipulatesOtherFields = options.some(
    item => item.requireDetails || Array.isArray(item.selectActions)
  )
  const adjustForMoreColumns =
    areMoreColumnsAvailable && numberOfColumns && numberOfColumns > 1

  return (
    <FastField
      name={id}
      {...muBag}
      useMultiuser={useMultiuser}
      langVersion={langVersion}
      required={required}
      disabled={disabled}
      isConnected={isConnected}
      numberOfColumns={numberOfColumns}
      shouldUpdate={(nextProps, currentProps) => {
        let checkValuesBool = false
        if (manipulatesOtherFields) {
          checkValuesBool = !_.isEqual(
            nextProps.formik.values,
            currentProps.formik.values
          )
        }
        return (
          checkShouldUpdateForMultiuserField(nextProps, currentProps) ||
          nextProps.clearOptionSelected !== currentProps.clearOptionSelected ||
          nextProps.langVersion !== currentProps.langVersion ||
          checkValuesBool ||
          nextProps.numberOfColumns !== currentProps.numberOfColumns ||
          nextProps.name !== currentProps.name
        )
      }}
    >
      {({ field, meta, form }) => {
        const { setValues, setFieldValue, values } = form
        const isError = Boolean(
          (meta.touched || hasFieldValue(field.value)) && meta.error)

        return (
          <FormControl component='fieldset' style={{ width: '100%' }}>
            <RadioGroup
              ref={formGroupRef}
              style={{
                width: '100%',
                ...(adjustForMoreColumns && {
                  flexDirection: 'row',
                  marginLeft: 25,
                  justifyContent: 'flex-start'
                }),
                ...(isRow && { flexDirection: 'row', flexWrap: 'wrap' })
              }}
              value={field.value || ''}
            >
              {options.map((item) => {
                const { title, tooltip, orgIndex } = item
                const index = orgIndex
                const defaultOpt = 'option' + index
                const optionValue = isConnected || item?.apiValue?.includes('fake-api')
                  ? item?.apiValue || defaultOpt
                  : defaultOpt
                const width = isRow
                  ? 'auto'
                  : adjustForMoreColumns
                    ? numberOfColumns === 2
                      ? '45%'
                      : numberOfColumns === 3
                        ? '30%'
                        : '100%'
                    : '100%'

                return (
                  <div key={index}>
                    <FormControlLabel
                      style={{ width }}
                      key={index}
                      disabled={disabled}
                      value={optionValue}
                      control={
                        <Radio
                          onChange={e => {
                            const toSet = { ...values }
                            const oldValue = toSet[id]

                            if (manipulatesOtherFields) {
                              let lastOptionActions = []
                              if (oldValue) {
                                options.some(option => {
                                  const index = option.orgIndex
                                  const defaultOpt = 'option' + index
                                  const optionValue = isConnected
                                    ? option.apiValue || defaultOpt
                                    : defaultOpt
                                  if (oldValue === optionValue) {
                                    lastOptionActions = option.selectActions
                                  }
                                  return oldValue === optionValue
                                })
                              }
                              toSet[id] = e.target.value
                              toSet[otherId] = ''
                              const modified = handleSelectActions({
                                toSet,
                                checked: true,
                                selectActions: item.selectActions,
                                lastOptionActions
                              })
                              setValues(toSet)
                              if (useMultiuser) {
                                commitChangeToMultipleFields({
                                  array: [...modified, id, otherId].map(id => [
                                    id,
                                    toSet[id]
                                  ]),
                                  ...muBag
                                })
                              }
                            } else {
                              setFieldValue(id, e.target.value)
                              if (useMultiuser) {
                                commitChangeToMultipleFields({
                                  array: [[id, e.target.value]],
                                  ...muBag
                                })
                              }
                            }
                          }}
                          sx={{
                            ...(isError ? { color: 'error.main' } : {})
                          }}
                        />
                      }
                      label={
                        <div>
                          {parseLabel(title)}
                          {tooltip && <TooltipLabelIcon tooltip={tooltip} />}
                        </div>
                      }
                    />
                    {Boolean(
                      field.value === optionValue && item.requireDetails
                    ) && (
                      <div
                        style={{
                          marginTop: 5,
                          marginBottom: 5,
                          width: adjustForMoreColumns && '100%'
                        }}
                      >
                        <RequireDetailsField
                          item={item}
                          useMultiuser={useMultiuser}
                          muBag={muBag}
                          id={'other.' + id}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </RadioGroup>
            {isError && <Grid item xs>
              <ErrorLabel label={<Trans>{meta.error}</Trans>} />
                        </Grid>}
          </FormControl>
        )
      }}
    </FastField>
  )
}

const PicklistMultiselectMemoized = ({
  id,
  options,
  langVersion,
  required,
  requiredAll,
  disabled,
  maxOptions,
  isConnected,
  clearOptionSelected,
  muBag,
  useMultiuser,
  numberOfColumns,
  formGroupRef,
  isRow,
  areMoreColumnsAvailable
}) => {
  const manipulatesOtherFields = options.some(
    item => item.requireDetails || Array.isArray(item.selectActions)
  )

  const adjustForMoreColumns =
    areMoreColumnsAvailable && numberOfColumns && numberOfColumns > 1

  return (
    <FastField
      name={id}
      {...muBag}
      useMultiuser={useMultiuser}
      langVersion={langVersion}
      required={required}
      disabled={disabled}
      maxOptions={maxOptions}
      isConnected={isConnected}
      clearOptionSelected={clearOptionSelected}
      options={options}
      numberOfColumns={numberOfColumns}
      shouldUpdate={(nextProps, currentProps) => {
        let checkValuesBool = false
        if (manipulatesOtherFields) {
          checkValuesBool = !_.isEqual(
            nextProps.formik.values,
            currentProps.formik.values
          )
        }
        return (
          checkShouldUpdateForMultiuserField(nextProps, currentProps) ||
          nextProps.clearOptionSelected !== currentProps.clearOptionSelected ||
          nextProps.langVersion !== currentProps.langVersion ||
          nextProps.options !== currentProps.options ||
          checkValuesBool ||
          nextProps.numberOfColumns !== currentProps.numberOfColumns ||
          nextProps.name !== currentProps.name
        )
      }}
    >
      {({ field, meta, form }) => {
        const { setValues, setFieldTouched, values, setFieldValue } = form
        const listValue = field.value || []
        const isError = Boolean(
          (meta.touched || hasFieldValue(listValue)) && meta.error)
        return (
          <FormControl component='fieldset' style={{ width: '100%' }}>
            <FormGroup
              ref={formGroupRef}
              style={{
                width: '100%',
                ...(adjustForMoreColumns && {
                  flexDirection: 'row',
                  justifyContent: 'flex-start'
                }),
                ...(isRow && { flexDirection: 'row', flexWrap: 'wrap' })
              }}
            >
              {options.map(item => {
                const { requireDetails, title, tooltip } = item
                const index = item.orgIndex
                const defaultOpt = 'option' + index
                const optionValue = isConnected
                  ? item.apiValue || defaultOpt
                  : defaultOpt
                let disableOption =
                  disabled ||
                  Boolean(clearOptionSelected && listValue[0] !== optionValue)
                const checked = listValue.includes(optionValue)
                if (!disableOption && maxOptions) {
                  disableOption =
                    listValue.length >= Number(maxOptions) && !checked
                }
                const width = isRow
                  ? 'auto'
                  : adjustForMoreColumns
                    ? numberOfColumns === 2
                      ? '50%'
                      : numberOfColumns === 3
                        ? '33.3%'
                        : '100%'
                    : '100%'

                return (
                  <Grid
                    container
                    direction='column'
                    key={index}
                    style={{ width }}
                  >
                    <FormControlLabel
                      disabled={disableOption}
                      key={index}
                      value={optionValue}
                      onChange={e => {
                        const { value } = e.currentTarget
                        if (!meta.touched) {
                          setFieldTouched(id)
                        }
                        const newValues = { ...values }
                        const oldValue = [...listValue]
                        let newList = [...listValue]
                        const otherId = 'other.' + id + '.' + index
                        let modified = []
                        if (newList.includes(value)) {
                          newList.splice(newList.indexOf(value), 1)
                          if (requireDetails) {
                            newValues[otherId] = ''
                          }
                        } else {
                          if (item.clearOnSelect) {
                            newList = [value]
                          } else {
                            newList.push(value)
                          }
                        }

                        if (manipulatesOtherFields) {
                          newValues[id] = newList
                          options.some(option => {
                            const index = option.orgIndex
                            const defaultOpt = 'option' + index
                            const optionValue = isConnected
                              ? option.apiValue || defaultOpt
                              : defaultOpt
                            const wasSelected =
                              oldValue.includes(optionValue) &&
                              !newValues[id].includes(optionValue)
                            if (wasSelected) {
                              modified = handleSelectActions({
                                toSet: newValues,
                                checked: newValues[id].includes(value),
                                lastOptionActions: option.selectActions,
                                selectActions: item.selectActions
                              })
                            }
                            return wasSelected
                          })
                          setValues(newValues)
                          if (useMultiuser) {
                            commitChangeToMultipleFields({
                              array: [...modified, id, otherId].map(id => [
                                id,
                                newValues[id]
                              ]),
                              ...muBag
                            })
                          }
                        } else {
                          setFieldValue(id, newList)
                          if (useMultiuser) {
                            commitChangeToMultipleFields({
                              array: [[id, newList]],
                              ...muBag
                            })
                          }
                        }
                      }}
                      checked={checked}
                      control={<Checkbox />}
                      label={
                        <>
                          <RequiredLabelPrefix required={requiredAll} />
                          {parseLabel(title || '')}
                          {tooltip && <TooltipLabelIcon tooltip={tooltip} />}
                        </>
                      }
                    />
                    {Boolean(
                      listValue.includes(optionValue) && requireDetails
                    ) && (
                      <div
                        style={{
                          marginTop: 5,
                          marginBottom: 5
                        }}
                      >
                        <RequireDetailsField
                          item={item}
                          useMultiuser={useMultiuser}
                          muBag={muBag}
                          id={'other.' + id + '.' + index}
                          disabled={disabled}
                          displayFieldHistoryIcon
                        />
                      </div>
                    )}
                  </Grid>
                )
              })}
            </FormGroup>
            {isError && <Grid item xs>
              <ErrorLabel label={
                requiredAll
                  ? <Trans>You must select all checkboxes!</Trans>
                  : <Trans>{meta.error}</Trans>
              }
              />
                        </Grid>}
          </FormControl>
        )
      }}
    </FastField>
  )
}

const RequireDetailsField = ({ item, ...props }) => {
  let specifyLimit
  if (item && item.connectedFieldDetails) {
    specifyLimit = item.connectedFieldDetails.length
  }

  return (
    <>
      <Trans>Please, specify: </Trans>
      <TextFieldWithFormik
        {...props}
        name={props.id}
        // inputProps={{
        //   maxLength: specifyLimit && Number(specifyLimit)
        // }}
        limit={specifyLimit}
      />
    </>
  )
}

const picklistTranslations = {
  Yes: msg`Yes`,
  No: msg`No`,
  'Non-profit housing': msg`Non-profit housing`,
  'Public housing': msg`Public housing`,
  'Transitional housing': msg`Transitional housing`,
  'On-reserve Indigenous housing': msg`On-reserve Indigenous housing`,
  'Off-reserve Indigenous housing': msg`Off-reserve Indigenous housing`,
  'Other housing provider': msg`Other housing provider`,
  'Community housing sector organization': msg`Community housing sector organization`,
  'Community economic development organization': msg`Community economic development organization`,
  'Funding agency': msg`Funding agency`,
  'Consulting firm': msg`Consulting firm`,
  Foundation: msg`Foundation`,
  'Registered charity': msg`Registered charity`,
  Media: msg`Media`,
  'Co-operative housing': msg`Co-operative housing`,
  'Cooperative housing': msg`Cooperative housing`,
  'Supportive housing': msg`Supportive housing`,
  'Shelter and other short-term/ emergency housing services': msg`Shelter and other short-term/ emergency housing services`,
  'Community Land Trust': msg`Community Land Trust`,
  'Tenant association': msg`Tenant association`,
  'Grassroots organization': msg`Grassroots organization`,
  'Government structure': msg`Government structure`,
  'Professional organization': msg`Professional organization`,
  'For-profit enterprise': msg`For-profit enterprise`,
  'Non-profit organization': msg`Non-profit organization`,
  'Academic or research institution': msg`Academic or research institution`,
  Other: msg`Other`,
  'Support services': msg`Support services`,
  'Tenant rights': msg`Tenant rights`,
  'Harm reduction': msg`Harm reduction`,
  'Meal services': msg`Meal services`,
  'Children and youth services': msg`Children and youth services`,
  Advocacy: msg`Advocacy`,
  'Financial support': msg`Financial support`,
  'Mental health': msg`Mental health`,
  'Employment services': msg`Employment services`,
  'Indigenous-owned': msg`Indigenous-owned`,
  'Indigenous-led': msg`Indigenous-led`,
  'Indigenous focus (primary demographic served)': msg`Indigenous focus (primary demographic served)`,
  'Indigenous aware': msg`Indigenous aware`,
  None: msg`None`,
  Families: msg`Families`,
  'Single-parent families': msg`Single-parent families`,
  'Persons living alone': msg`Persons living alone`,
  Seniors: msg`Seniors`,
  'Low-income households': msg`Low-income households`,
  'Women and their children': msg`Women and their children`,
  'Youth/ young adults': msg`Youth/ young adults`,
  'At-risk youth': msg`At-risk youth`,
  'LGBTQIA2S+': msg`LGBTQIA2S+`,
  'First Nations': msg`First Nations`,
  Inuit: msg`Inuit`,
  Métis: msg`Métis`,
  'All Indigenous Peoples': msg`All Indigenous Peoples`,
  'Black persons/ communities': msg`Black persons/ communities`,
  'Racialized persons/ communities': msg`Racialized persons/ communities`,
  'Newcomers and refugees': msg`Newcomers and refugees`,
  'People experiencing homelessness': msg`People experiencing homelessness`,
  'Formerly incarcerated people': msg`Formerly incarcerated people`,
  'People fleeing domestic violence': msg`People fleeing domestic violence`,
  'People living with mental illness (e.g. Bipolar disorder, Schizophrenia, etc.)': msg`People living with mental illness (e.g. Bipolar disorder, Schizophrenia, etc.)`,
  'People living with addictions': msg`People living with addictions`,
  'People with physical disabilities (e.g. reduced mobility, visually impaired, etc.)': msg`People with physical disabilities (e.g. reduced mobility, visually impaired, etc.)`,
  "People with developmental disabilities (e.g. Down's syndrome, Fetal Alcohol Spectrum Disorder, Autism, etc.)": msg`People with developmental disabilities (e.g. Fetal Alcohol Spectrum Disorder, Autism, etc.)`,
  'People experiencing social isolation': msg`People experiencing social isolation`,
  Veterans: msg`Veterans`,
  'Rental/ housing charge income': msg`Rental/ housing charge income`,
  'Canada Mortgage and Housing Corporation': msg`Canada Mortgage and Housing Corporation`,
  'Other federal government grants': msg`Other federal government grants`,
  'Provincial or territorial government': msg`Provincial or territorial government`,
  Municipality: msg`Municipality`,
  'Donations/ fundraising': msg`Donations/ fundraising`,
  'Canada Mortgage and Housing Corporation (CMHC)': msg`Canada Mortgage and Housing Corporation (CMHC)`,
  'Federation of Canadian Municipalities (FCM)': msg`Federation of Canadian Municipalities (FCM)`,
  'Co-operative Housing Federation of Canada (CHF Canada)': msg`Co-operative Housing Federation of Canada (CHF Canada)`,
  'Other national housing association or sector organization': msg`Other national housing association or sector organization`,
  'Provincial or territorial housing organization': msg`Provincial or territorial housing organization`,
  'Regional or local housing organization': msg`Regional or local housing organization`,
  'Centre website': msg`Centre website`,
  'Centre newsletter': msg`Centre newsletter`,
  "Centre's social media": msg`Centre's social media`,
  "Outreach from one of the Centre's Program Managers": msg`Outreach from one of the Centre's Program Managers`,
  'From a previous applicant': msg`From a previous applicant`,
  'Word of mouth': msg`Word of mouth`,
  'Conferences/networking events': msg`Conferences/networking events`,
  'Diversity, equity, and inclusion, including First Voice and underrepresented groups': msg`Diversity, equity, and inclusion, including First Voice and underrepresented groups`,
  'Addressing systemic barriers to housing': msg`Addressing systemic barriers to housing`,
  'Advancing capacity building opportunities within the sector': msg`Advancing capacity building opportunities within the sector`,
  'Leveraging regional, government, and cross-sectoral resources and supports': msg`Leveraging regional, government, and cross-sectoral resources and supports`,
  'Providing navigation and support services to people in housing need': msg`Providing navigation and support services to people in housing need`,
  'New build': msg`New build`,
  Retrofit: msg`Retrofit`,
  Owned: msg`Owned`,
  'Long-term lease': msg`Long-term lease`,
  'Agreement in place': msg`Agreement in place`,
  'No agreement in place': msg`No agreement in place`,
  Roads: msg`Roads`,
  Sewer: msg`Sewer`,
  Water: msg`Water`,
  Electricity: msg`Electricity`,
  Provincial: msg`Provincial`,
  Municipal: msg`Municipal`,
  Regional: msg`Regional`,
  'Province- or Territory-wide': msg`Province- or Territory-wide`,
  Neighbourhood: msg`Neighbourhood`,
  National: msg`National`,
  'Halifax regional municipality': msg`Halifax regional municipality`,
  'Cape Breton regional municipality': msg`Cape Breton regional municipality`,
  'Small population centre (population between 1,000 and 29,000, and population density over 400 people per square kilometre)': msg`Small population centre (population between 1,000 and 29,000, and population density over 400 people per square kilometre)`,
  'Rural (all areas outside population centres)': msg`Rural (all areas outside population centres)`,
  'Large urban centre (city with population over 100,000)': msg`Large urban centre (city with population over 100,000)`,
  'Medium population centre (population between 30,000 and 99,999)': msg`Medium population centre (population between 30,000 and 99,999)`,
  'Shelter/ transitional housing': msg`Shelter/ transitional housing`,
  'Social housing': msg`Social housing`,
  'Reducing homelessness by identifying housing options': msg`Reducing homelessness by identifying housing options`,
  'Increasing stock of affordable housing for vulnerable populations and underrepresented groups': msg`Increasing stock of affordable housing for vulnerable`,
  'Renovating or repurposing existing buildings for use in the housing continuum': msg`Renovating or repurposing existing buildings for use in the housing continuum`,
  'Leveraging solutions to ensure improvement of both the social and economic conditions of Inuit': msg`Leveraging solutions to ensure improvement of both the social and economic conditions of Inuit`,
  'Collaborating with communities and partners in poverty reduction': msg`Collaborating with communities and partners in poverty reduction`,
  'Advancing capacity building opportunities within the community housing sector': msg`Advancing capacity building opportunities within the community housing sector`,
  'Leveraging resources and supports to expand community housing provision in Nunavut': msg`Leveraging resources and supports to expand community housing provision in Nunavut`,
  "Reducing the sector's environmental footprint": msg`Reducing the sector's environmental footprint`,
  'Answering the gaps in service for Indigenous communities': msg`Answering the gaps in service for Indigenous communities`,
  'Supporting innovative and sustainable business practices': msg`Supporting innovative and sustainable business practices`,
  'Increasing social inclusion and community engagement': msg`Increasing social inclusion and community engagement`,
  'Facilitating growth of the community housing sector': msg`Facilitating growth of the community housing sector`,
  'Non applicable': msg`Non applicable`,
  Unsure: msg`Unsure`,
  'up to 1.5 km': msg`up to 1.5 km`,
  '1.5 to 5 km': msg`1.5 to 5 km`,
  '5 to 10 km': msg`5 to 10 km`,
  '10 to 20 km': msg`10 to 20 km`,
  'more than 20 km': msg`more than 20 km`,
  Walking: msg`Walking`,
  'Public bus': msg`Public bus`,
  'Community transit': msg`Community transit`,
  Cab: msg`Cab`,
  'Car only': msg`Car only`,
  'Planning/ initiation': msg`Planning/ initiation`,
  Execution: msg`Execution`,
  Closure: msg`Closure`,
  Feasibility: msg`Feasibility`,
  'Pre-Development': msg`Pre-Development`,
  'Tenant rights (promotion of rights and responsibilities, eviction prevention, etc.)': msg`Tenant rights (promotion of rights and responsibilities, eviction prevention, etc.)`,
  'Tenant advocacy (representing tenant interests in politics, etc.)': msg`Tenant advocacy (representing tenant interests in politics, etc.)`,
  'Tenant engagement (tenant consultations, tenant-led initiatives, etc.)': msg`Tenant engagement (tenant consultations, tenant-led initiatives, etc.)`,
  'Community involvement (block parties, communal gardens, etc.)': msg`Community involvement (block parties, communal gardens, etc.)`,
  'Financial literacy (budget planning, collaborative budgeting, etc.)': msg`Financial literacy (budget planning, collaborative budgeting, etc.)`,
  'Support services (disability support, mental health services, etc.)': msg`Support services (disability support, mental health services, etc.)`,
  'Social inclusion (intercultural awareness, integration services, accessibility, diversity in governance, etc.)': msg`Social inclusion (intercultural awareness, integration services, accessibility, diversity in governance, etc.)`,
  'Workshops and trainings (skills development, teamwork, etc.)': msg`Workshops and trainings (skills development, teamwork, etc.)`,
  'Partnership development (social economy, community roundtables, cross-sectorial collaborations, etc.)': msg`Partnership development (social economy, community roundtables, cross-sectorial collaborations, etc.)`,
  'Community housing advocacy (representing the interests of the sector, etc.)': msg`Community housing advocacy (representing the interests of the sector, etc.)`,
  'Governance (board of directors, organizational structure, etc.)': msg`Governance (board of directors, organizational structure, etc.)`,
  'Indigenous knowledge (resources, services for tenants, housing providers, etc.)': msg`Indigenous knowledge (resources, services for tenants, housing providers, etc.)`,
  'Environmental sustainability (implementation of environmental policy, environmental footprint reduction, etc.)': msg`Environmental sustainability (implementation of environmental policy, environmental footprint reduction, etc.)`,
  'Energy efficiency (retrofit, green building, etc.)': msg`Energy efficiency (retrofit, green building, etc.)`,
  'Operations/ administration (rental management, rent collection, enforcing tenant rules and regulations, etc.)': msg`Operations/ administration (rental management, rent collection, enforcing tenant rules and regulations, etc.)`,
  'Financial management/ accounting (income analysis, feasibility study, financial planning, etc.)': msg`Financial management/ accounting (income analysis, feasibility study, financial planning, etc.)`,
  'Funding/ financing (grants, refinancing, public financing, etc.)': msg`Funding/ financing (grants, refinancing, public financing, etc.)`,
  'Housing development and redevelopment (business plan, construction, urban planning, etc.)': msg`Housing development and redevelopment (business plan, construction, urban planning, etc.)`,
  'Asset and property management (maintenance, etc.)': msg`Asset and property management (maintenance, etc.)`,
  'Legal or policy knowledge (co-ownership structures, etc.)': msg`Legal or policy knowledge (co-ownership structures, etc.)`,
  'Professional associations (buying groups, resource groups, etc.)': msg`Professional associations (buying groups, resource groups, etc.)`,
  'Workshops and training (skills development, teamwork, etc.)': msg`Workshops and training (skills development, teamwork, etc.)`
}

export function formPicklistValueToExportText (value, _) {
  if (!value) {
    return ''
  }

  if (Array.isArray(value)) {
    return value.map(v => {
      const translation = picklistTranslations[v]

      return translation ? i18n._(translation) : v
    }).join(', ')
  } else {
    const translation = picklistTranslations[value]

    return translation ? i18n._(translation) : value
  }
}
